import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Spinner, StatusBadge, Switch } from '../../components/ui';

interface AdminTier {
  id: string;
  name: string;
  slug: string;
  monthly_characters: number;
  price_monthly: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  is_active: boolean;
}

export function AdminTiers() {
  const [tiers, setTiers] = useState<AdminTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<AdminTier | null>(null);
  const [editValues, setEditValues] = useState<Partial<AdminTier>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoading(true);
    const result = await api.adminGetTiers();
    if (result.success && result.data) {
      setTiers(result.data);
    } else {
      setError(result.error?.message || 'Failed to load tiers');
    }
    setLoading(false);
  };

  const handleEdit = (tier: AdminTier) => {
    setEditingTier(tier);
    setEditValues({
      stripe_price_id_monthly: tier.stripe_price_id_monthly || '',
      stripe_price_id_yearly: tier.stripe_price_id_yearly || '',
      is_active: tier.is_active,
    });
    setMessage(null);
  };

  const handleSave = async () => {
    if (!editingTier) return;

    setSaving(true);
    const result = await api.adminUpdateTier(editingTier.id, editValues);
    if (result.success) {
      setMessage({ type: 'success', text: 'Tier updated successfully' });
      setEditingTier(null);
      await loadTiers();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to update tier' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-[#F0928C] text-sm">{error}</p>
        <button onClick={loadTiers} className="btn-ghost mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="fx">
      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-xl border text-[13px] font-medium ${
            message.type === 'success'
              ? 'border-[#2E8B57]/40 bg-[#2E8B57]/10 text-[#7BD0A0]'
              : 'border-[#D8362F]/40 bg-[#D8362F]/10 text-[#F0928C]'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiers.map((tier) => (
          <div key={tier.id} className="card lift flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h3 className="m-0 font-display font-extrabold text-white text-[16px] uppercase tracking-[0.04em]">
                  {tier.name}
                </h3>
                <p className="mt-1 mb-0 text-mist-500 text-xs font-mono truncate">{tier.slug}</p>
              </div>
              <StatusBadge status={tier.is_active ? 'active' : 'inactive'} />
            </div>

            <p className="m-0 mb-4 font-display font-extrabold text-white text-[26px] leading-none">
              ${(tier.price_monthly / 100).toLocaleString()}
              <span className="text-mist-500 text-[13px] font-sans font-semibold">/mo</span>
            </p>

            <div className="mb-5">
              <div className="flex items-center justify-between gap-3 py-2.5 border-t border-ink-800">
                <span className="text-mist-500 text-[13px]">Characters / month</span>
                <span className="text-mist-200 text-[13px] font-mono">
                  {tier.monthly_characters.toLocaleString()}
                </span>
              </div>
            </div>

            {editingTier?.id === tier.id ? (
              <div className="flex flex-col gap-4 border-t border-ink-800 pt-4 mt-auto">
                <div>
                  <label className="label">Monthly price ID</label>
                  <input
                    type="text"
                    value={editValues.stripe_price_id_monthly || ''}
                    onChange={(e) =>
                      setEditValues({ ...editValues, stripe_price_id_monthly: e.target.value })
                    }
                    placeholder="price_..."
                    className="input py-2.5 text-[13px] font-mono"
                  />
                </div>
                <div>
                  <label className="label">Yearly price ID</label>
                  <input
                    type="text"
                    value={editValues.stripe_price_id_yearly || ''}
                    onChange={(e) =>
                      setEditValues({ ...editValues, stripe_price_id_yearly: e.target.value })
                    }
                    placeholder="price_..."
                    className="input py-2.5 text-[13px] font-mono"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-mist-300 text-[13px] font-semibold">Active</span>
                  <Switch
                    checked={editValues.is_active ?? tier.is_active}
                    onChange={() =>
                      setEditValues({
                        ...editValues,
                        is_active: !(editValues.is_active ?? tier.is_active),
                      })
                    }
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-grad flex-1 py-[11px] text-xs"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingTier(null)} className="btn-ghost">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-t border-ink-800 pt-4 mt-auto">
                <div className="flex flex-col gap-3 mb-4">
                  <div className="min-w-0">
                    <span className="block text-mist-500 text-[11px] font-semibold uppercase tracking-[0.1em]">
                      Monthly price ID
                    </span>
                    <p className="mt-1 mb-0 text-[12.5px] font-mono text-mist-300 truncate">
                      {tier.stripe_price_id_monthly || (
                        <span style={{ color: 'var(--a-hi)' }}>Not set</span>
                      )}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="block text-mist-500 text-[11px] font-semibold uppercase tracking-[0.1em]">
                      Yearly price ID
                    </span>
                    <p className="mt-1 mb-0 text-[12.5px] font-mono text-mist-300 truncate">
                      {tier.stripe_price_id_yearly || (
                        <span style={{ color: 'var(--a-hi)' }}>Not set</span>
                      )}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleEdit(tier)} className="btn-ghost w-full">
                  Edit tier
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {tiers.length === 0 && (
        <p className="text-center text-mist-500 text-sm py-10 m-0">No tiers found</p>
      )}

      <div
        className="mt-4 rounded-2xl border p-5"
        style={{
          borderColor: 'rgba(31,143,224,.3)',
          background: 'linear-gradient(150deg,rgba(31,143,224,.08),#17171B 60%)',
        }}
      >
        <div className="flex gap-3">
          <svg
            className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#7CC0F0]"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-[13px] text-mist-300">
            <p className="m-0 mb-1 font-semibold text-[#7CC0F0]">Stripe price IDs</p>
            <p className="m-0">
              To enable subscriptions, create products and prices in your Stripe dashboard, then
              paste the price IDs here. Price IDs start with{' '}
              <code className="font-mono text-[11.5px] text-mist-200 bg-ink-950 border border-ink-700 rounded px-1.5 py-0.5">
                price_
              </code>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
